import { CERITA_FREE_QUOTA, DEFAULT_REMINDER, QURAN_CREDIT, SOURCE_HOMES, WORDS_CREDIT } from '@iqrapath/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing, BookOpenText, ChevronLeft, ChevronRight, FileText, KeyRound, LifeBuoy, LogOut, MessageCircleHeart, MoreVertical, Pencil, Share, Smartphone } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ReminderTime } from '../../components/ReminderTime.tsx'
import { Sheet } from '../../components/Sheet.tsx'
import { SourceLink } from '../../components/SourceLink.tsx'
import { ApiError, api, deleteAccount, logout, putProfile, type Prefs, type Session } from '../../lib/api.ts'
import { canPromptInstall, isIos, isStandalone, needsHomeScreenForNotifications, notificationState, promptInstall } from '../../lib/platform.ts'
import { PUSH_KEY, PUSH_OUTCOME_MSG, currentSubscription, disablePush, enablePush, getPushInfo, pushSupported, sendTestPush } from '../../lib/push.ts'
import { FONT_LABELS, useFontScale, useLatinOn, type FontScale } from '../../lib/prefs.ts'
import { ME_KEY, updatePrefs, useSession } from '../../lib/queries.ts'
import { useToast } from '../../lib/toast.ts'
import type { Quota } from '../cerita/cerita.ts'

const JALUR: { key: Prefs['jalur']; label: string }[] = [
  { key: 'maknai', label: 'Sudah lancar' },
  { key: 'baca', label: 'Masih belajar' },
]

type PushView = 'ios' | 'unsupported' | 'checking' | 'unavailable' | 'denied' | 'off' | 'on'

/** Whether reminders can reach this browser, and the taps that change it. */
function usePush(show: (msg: string) => void) {
  const qc = useQueryClient()
  const usable = pushSupported() && !needsHomeScreenForNotifications()
  const info = useQuery({ queryKey: PUSH_KEY, queryFn: getPushInfo, enabled: usable, staleTime: 60_000 })
  const [subscribed, setSubscribed] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (usable) void currentSubscription().then((s) => setSubscribed(s != null))
  }, [usable])

  const view: PushView = needsHomeScreenForNotifications()
    ? 'ios'
    : !pushSupported()
      ? 'unsupported'
      : !info.data || subscribed === null
        ? 'checking'
        : !info.data.enabled
          ? 'unavailable'
          : notificationState() === 'denied'
            ? 'denied'
            : notificationState() === 'granted' && subscribed
              ? 'on'
              : 'off'

  const turnOn = async () => {
    setBusy(true)
    const r = await enablePush()
    setBusy(false)
    setSubscribed(r === 'on')
    show(PUSH_OUTCOME_MSG[r])
    void qc.invalidateQueries({ queryKey: PUSH_KEY })
  }
  const test = async () => {
    setBusy(true)
    try {
      await sendTestPush()
      show('Notifikasi percobaan terkirim. Cek bilah notifikasi.')
    } catch (e) {
      show(e instanceof ApiError ? e.message : 'Belum terkirim. Coba lagi.')
    } finally {
      setBusy(false)
    }
  }
  return { view, busy, turnOn, test }
}

function PushNote({ push, onInstallHelp }: { push: ReturnType<typeof usePush>; onInstallHelp: () => void }) {
  switch (push.view) {
    case 'ios':
      return (
        <p className="help set-note">
          Di iPhone, pengingat hanya muncul kalau IqraPath dibuka dari layar utama.{' '}
          <button type="button" className="link-btn" onClick={onInstallHelp}>Lihat caranya</button>
        </p>
      )
    case 'unsupported':
      return <p className="help set-note">Browser ini belum mendukung notifikasi.</p>
    case 'checking':
      return null
    case 'unavailable':
      return <p className="help set-note">Notifikasi belum tersedia untuk sementara. Jam pengingatmu tetap tersimpan.</p>
    case 'denied':
      return <p className="help set-note">Izin notifikasi ditolak di browser ini. Izinkan lewat pengaturan situs di browser, lalu buka lagi halaman ini.</p>
    case 'off':
      return (
        <p className="help set-note">
          Notifikasi belum aktif di perangkat ini.{' '}
          <button type="button" className="link-btn" disabled={push.busy} onClick={push.turnOn}>Aktifkan</button>
        </p>
      )
    case 'on':
      return (
        <p className="help set-note">
          Notifikasi aktif di perangkat ini.{' '}
          <button type="button" className="link-btn" disabled={push.busy} onClick={push.test}>Kirim percobaan</button>
        </p>
      )
  }
}

function InstallHelp({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="Simpan ke layar utama" onClose={onClose}>
      <p className="muted">IqraPath bisa dibuka seperti aplikasi biasa, tanpa toko aplikasi.</p>
      {isIos() ? (
        <ol className="steps">
          <li>Buka IqraPath di <b>Safari</b>.</li>
          <li>Ketuk <Share size={16} aria-label="tombol Bagikan" /> di bawah layar.</li>
          <li>Pilih <b>Tambah ke Layar Utama</b>, lalu <b>Tambah</b>.</li>
        </ol>
      ) : (
        <ol className="steps">
          <li>Buka IqraPath di <b>Chrome</b>.</li>
          <li>Ketuk <MoreVertical size={16} aria-label="tombol menu" /> di pojok kanan atas.</li>
          <li>Pilih <b>Instal aplikasi</b> atau <b>Tambahkan ke layar utama</b>.</li>
        </ol>
      )}
      <button className="btn btn-primary btn-block" type="button" onClick={onClose} style={{ marginTop: 14 }}>Mengerti</button>
    </Sheet>
  )
}

/** /pengaturan (docs/PRD.md §6.9, UX-PLAYBOOK §7.11). */
export function SettingsPage() {
  const { data } = useSession()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const fs = useFontScale()
  const latin = useLatinOn()
  const [toast, show] = useToast()
  const [sheet, setSheet] = useState<null | 'name' | 'install' | 'delete'>(null)
  const [name, setName] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const quota = useQuery({ queryKey: ['cerita', 'kuota'], queryFn: () => api<{ ok: true; quota: Quota }>('GET', '/api/cerita/kuota'), staleTime: 60_000 })
  const push = usePush(show)
  if (data?.state !== 'ok') return null
  const { user, license, prefs } = data.me
  const reminder = prefs?.reminder ?? DEFAULT_REMINDER
  const jalur = prefs?.jalur ?? 'maknai'

  // Applied on this device at once; the account copy (next device) is saved in the background.
  const save = (patch: Partial<Prefs>) => void updatePrefs(qc, patch).catch(() => show('Belum tersimpan. Cek koneksi, lalu coba lagi.'))
  const setSession = (fn: (s: Extract<Session, { state: 'ok' }>) => Session) => qc.setQueryData<Session>(ME_KEY, (s) => (s?.state === 'ok' ? fn(s) : s))

  // switching the reminder on is a tap: the right moment to ask for permission on this browser
  const toggleReminder = () => {
    const enabled = !reminder.enabled
    save({ reminder: { ...reminder, enabled } })
    if (enabled && (push.view === 'off' || push.view === 'checking')) void push.turnOn()
  }
  const leave = async () => {
    // a shared phone must not keep getting this account's reminders
    await disablePush()
    await logout().catch(() => {})
    await qc.invalidateQueries({ queryKey: ME_KEY })
    navigate('/masuk', { replace: true })
  }
  const saveName = async () => {
    setBusy(true)
    try {
      const r = await putProfile(name)
      setSession((s) => ({ ...s, me: { ...s.me, user: { ...s.me.user, name: r.name } } }))
      setSheet(null)
      show('Nama tersimpan.')
    } catch (e) {
      show(e instanceof ApiError ? e.message : 'Belum tersimpan. Coba lagi.')
    } finally {
      setBusy(false)
    }
  }
  const install = async () => {
    if (canPromptInstall()) {
      const r = await promptInstall()
      if (r === 'accepted') show('IqraPath sedang dipasang di perangkatmu.')
      return
    }
    setSheet('install')
  }
  const removeAccount = async () => {
    setBusy(true)
    try {
      await deleteAccount()
      await disablePush({ server: false }) // the server copy went with the account
      qc.clear()
      navigate('/masuk', { replace: true })
    } catch (e) {
      setBusy(false)
      show(e instanceof ApiError ? e.message : 'Akun belum terhapus. Coba lagi.')
    }
  }

  const q = quota.data?.quota
  return (
    <div className="page settings">
      <Link className="back" to="/"><ChevronLeft size={18} aria-hidden="true" /> Beranda</Link>
      <h1>Pengaturan</h1>

      <div className="card profile">
        <div className="avatar" aria-hidden="true">{(user.name || user.email)[0]?.toUpperCase()}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <b>{user.name || 'Belum ada nama'}</b>
          <small className="muted">{user.email}</small>
        </div>
        <button type="button" className="icon-btn plain" aria-label="Ubah nama" onClick={() => { setName(user.name); setSheet('name') }}>
          <Pencil size={18} aria-hidden="true" />
        </button>
      </div>

      <h2 className="section-t">Pengingat harian</h2>
      <div className="card">
        <div className="set-row">
          <span className="ic" aria-hidden="true"><BellRing size={20} /></span>
          <div style={{ flex: 1 }}>
            <b id="remind-l">Ingatkan aku untuk ayat hari ini</b>
            <p className="help" style={{ marginTop: 2 }}>Satu notifikasi sehari, tidak lebih.</p>
          </div>
          <button type="button" role="switch" aria-checked={reminder.enabled} aria-labelledby="remind-l" className="switch" onClick={toggleReminder} />
        </div>
        {reminder.enabled && <ReminderTime hour={reminder.hour} minute={reminder.minute} onChange={(hour, minute) => save({ reminder: { enabled: true, hour, minute } })} />}
      </div>
      {reminder.enabled && <PushNote push={push} onInstallHelp={() => setSheet('install')} />}

      <h2 className="section-t">Bacaan</h2>
      <div className="seg" role="group" aria-label="Membaca huruf Arab">
        {JALUR.map((j) => (
          <button key={j.key} type="button" aria-pressed={jalur === j.key} onClick={() => save({ jalur: j.key })}>{j.label}</button>
        ))}
      </div>
      <p className="help">Membaca huruf Arab: pilih yang paling sesuai saat ini.</p>
      <div className="seg" role="group" aria-label="Ukuran huruf" style={{ marginTop: 12 }}>
        {FONT_LABELS.map((l, i) => (
          <button key={l} type="button" aria-pressed={fs === i} onClick={() => save({ fontScale: i as FontScale })}>
            <span style={{ fontSize: 15 + i * 3 }}>Aa</span>
            <small style={{ fontSize: '.72rem' }}>{l}</small>
          </button>
        ))}
      </div>
      <div className="card" style={{ marginTop: 12 }}>
        <div className="set-row">
          <div style={{ flex: 1 }}>
            <b id="latin-l">Tulisan latin</b>
            <p className="help" style={{ marginTop: 2 }}>Tampilkan cara baca latin di bawah teks Arab.</p>
          </div>
          <button type="button" role="switch" aria-checked={latin} aria-labelledby="latin-l" className="switch" onClick={() => save({ latinOn: !latin })} />
        </div>
      </div>

      <h2 className="section-t">Teman Cerita</h2>
      <div className="card">
        <Link to="/cerita" className="set-row" style={{ textDecoration: 'none' }}>
          <span className="ic" aria-hidden="true"><MessageCircleHeart size={20} /></span>
          <div style={{ flex: 1 }}>
            <b>{!q ? 'Teman Cerita' : !q.ai ? 'Jawaban otomatis dari kartu Pegangan' : `${q.left} dari ${CERITA_FREE_QUOTA} chat AI gratis tersisa`}</b>
            <p className="help" style={{ marginTop: 2 }}>
              {q && q.ai && q.left === 0 ? 'Setelah habis, jawaban diambil otomatis dari kartu Pegangan.' : 'Jawaban AI selalu diperiksa ke sumbernya dulu.'}
            </p>
          </div>
          <ChevronRight size={18} className="muted" aria-hidden="true" />
        </Link>
      </div>

      <h2 className="section-t">Aplikasi</h2>
      <div className="card">
        <div className="set-row">
          <span className="ic" aria-hidden="true"><Smartphone size={20} /></span>
          <div style={{ flex: 1 }}>
            <b>Simpan ke layar utama</b>
            <p className="help" style={{ marginTop: 2 }}>{isStandalone() ? 'Sudah terpasang di perangkat ini.' : 'Buka IqraPath langsung dari ikon, seperti aplikasi.'}</p>
          </div>
          {!isStandalone() && <button type="button" className="btn btn-ghost btn-sm" onClick={install}>Pasang</button>}
        </div>
        <Link to="/bantuan" className="set-row" style={{ textDecoration: 'none' }}>
          <span className="ic" aria-hidden="true"><LifeBuoy size={20} /></span>
          <div style={{ flex: 1 }}>
            <b>Bantuan</b>
            <p className="help" style={{ marginTop: 2 }}>Ada kendala atau saran? Kirim pesan ke tim IqraPath.</p>
          </div>
          <ChevronRight size={18} className="muted" aria-hidden="true" />
        </Link>
      </div>

      <h2 className="section-t">Lisensi</h2>
      <div className="card">
        <div className="set-row">
          <span className="ic" aria-hidden="true"><KeyRound size={20} /></span>
          <div style={{ flex: 1 }}>
            <b>{license.product}</b>
            <p className="help" style={{ marginTop: 2 }}>
              Kode <span className="num">••••-••••-••••-{license.keyLast4}</span> ·{' '}
              {license.expiresAt ? `berlaku sampai ${new Date(license.expiresAt).toLocaleDateString('id-ID')}` : 'berlaku selamanya'}
            </p>
          </div>
        </div>
      </div>

      <h2 className="section-t">Sumber</h2>
      <div className="card">
        {([
          [SOURCE_HOMES.kemenag, QURAN_CREDIT],
          [SOURCE_HOMES.qurancom, WORDS_CREDIT],
          [SOURCE_HOMES.hadeethenc, 'Hadis sahih, derajat & syarah: HadeethEnc.com (Ensiklopedia Hadis Terjemah)'],
        ] as const).map(([href, label]) => (
          <div key={href} className="set-row">
            <span className="ic" aria-hidden="true"><BookOpenText size={20} /></span>
            <SourceLink href={href} className="set-src">{label}</SourceLink>
          </div>
        ))}
      </div>

      <h2 className="section-t">Tentang</h2>
      <div className="card">
        {([['syarat', 'Syarat & Ketentuan'], ['privasi', 'Kebijakan Privasi'], ['disclaimer', 'Disclaimer']] as const).map(([slug, label]) => (
          <Link key={slug} to={`/legal/${slug}`} className="set-row" style={{ textDecoration: 'none' }}>
            <span className="ic" aria-hidden="true"><FileText size={20} /></span>
            <b style={{ flex: 1 }}>{label}</b>
            <ChevronRight size={18} className="muted" aria-hidden="true" />
          </Link>
        ))}
      </div>

      <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 22, color: 'var(--rose)' }} onClick={leave}>
        <LogOut size={18} aria-hidden="true" /> Keluar
      </button>
      <button type="button" className="link-btn set-danger" onClick={() => { setConfirm(''); setSheet('delete') }}>Hapus akun</button>

      {sheet === 'name' && (
        <Sheet title="Nama panggilan" onClose={() => setSheet(null)}>
          <form onSubmit={(e) => { e.preventDefault(); void saveName() }}>
            <label className="field">
              <span>Dipakai untuk menyapamu di Beranda</span>
              <input className="input" value={name} maxLength={60} autoComplete="given-name" onChange={(e) => setName(e.target.value)} placeholder="Contoh: Rina" />
            </label>
            <button type="submit" className="btn btn-primary btn-block" disabled={busy} style={{ marginTop: 14 }}>Simpan</button>
          </form>
        </Sheet>
      )}
      {sheet === 'install' && <InstallHelp onClose={() => setSheet(null)} />}
      {sheet === 'delete' && (
        <Sheet title="Hapus akun?" onClose={() => setSheet(null)}>
          <p className="muted">
            Semua data di akun ini akan dihapus permanen: progres ayat, jurnal, kartu tersimpan, dan riwayat Teman Cerita. Ini tidak bisa dibatalkan.
          </p>
          <p className="muted" style={{ marginTop: 8 }}>Kode lisensimu tetap berlaku. Kalau masuk lagi, kamu mulai dari akun baru.</p>
          <label className="field">
            <span>Ketik <b>HAPUS</b> untuk melanjutkan</span>
            <input className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" autoCapitalize="characters" />
          </label>
          <button type="button" className="btn btn-danger btn-block" disabled={confirm !== 'HAPUS' || busy} onClick={removeAccount} style={{ marginTop: 14 }}>
            Hapus akun selamanya
          </button>
          <button type="button" className="btn btn-ghost btn-block" onClick={() => setSheet(null)} style={{ marginTop: 8 }}>Batal</button>
        </Sheet>
      )}

      <div className="toast-slot" role="status">{toast && <p className="toast">{toast}</p>}</div>
    </div>
  )
}
